import React, { useState, useEffect, memo } from 'react';
import { Shield, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from '../axiosConfig';
import './KYCBadge.css';

const KYCBadge = memo(() => {
  const [kycData, setKycData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchKYCStatus();
  }, []);

  const fetchKYCStatus = async () => {
    try {
      const response = await axios.get('/kyc/status');
      if (response.data.success) {
        setKycData(response.data.kyc);
      }
    } catch (err) {
      console.error('Failed to fetch KYC status:', err);
    }
  };

  if (!kycData) {
    return null;
  }

  const getBadgeInfo = () => {
    // Level 3 - Fully Verified (Show Verified Stamp)
    if (kycData.status === 'APPROVED' && kycData.level === 'LEVEL_3') {
      return {
        type: 'verified',
        text: 'Verified',
        icon: '✓',
        color: '#10b981',
        isClickable: false
      };
    }

    // Level 2 - Approved (Show Upgrade Prompt)
    if (kycData.status === 'APPROVED' && kycData.level === 'LEVEL_2') {
      return {
        type: 'upgrade',
        text: 'Level 2',
        subtext: 'Upgrade to Level 3',
        color: '#3b82f6',
        isClickable: true
      };
    }

    // Level 1 - Approved (Show Upgrade Prompt)
    if (kycData.status === 'APPROVED' && kycData.level === 'LEVEL_1') {
      return {
        type: 'upgrade',
        text: 'Level 1',
        subtext: 'Upgrade to Level 2',
        color: '#8b5cf6',
        isClickable: true
      };
    }

    // Incomplete KYC - Show Stage Info
    if (['NOT_STARTED', 'IN_PROGRESS'].includes(kycData.status)) {
      const stageText = kycData.level === 'LEVEL_0' ? 'Start KYC' :
                       kycData.level === 'LEVEL_1' ? 'Complete Level 2' :
                       kycData.level === 'LEVEL_2' ? 'Complete Level 3' : 'Complete KYC';

      return {
        type: 'incomplete',
        text: `${kycData.progressPercentage || 0}% Done`,
        subtext: stageText,
        color: '#f59e0b',
        isClickable: true
      };
    }

    // Pending Review
    if (kycData.status === 'PENDING_REVIEW') {
      return {
        type: 'pending',
        text: 'Under Review',
        subtext: 'Please wait',
        color: '#6b7280',
        isClickable: false
      };
    }

    // Resubmission Required
    if (kycData.status === 'RESUBMISSION_REQUIRED') {
      return {
        type: 'resubmit',
        text: 'Action Required',
        subtext: 'Resubmit KYC',
        color: '#ef4444',
        isClickable: true
      };
    }

    return null;
  };

  const badgeInfo = getBadgeInfo();

  if (!badgeInfo) {
    return null;
  }

  // Verified Stamp for Level 3 (100% Complete)
  if (badgeInfo.type === 'verified') {
    return (
      <div
        className="kyc-verified-stamp"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          padding: '6px 12px',
          background: 'linear-gradient(135deg, #10b98120, #10b98110)',
          border: '2px solid #10b981',
          borderRadius: '20px',
          fontSize: '13px',
          fontWeight: '600',
          color: '#10b981',
          cursor: 'default',
          boxShadow: '0 2px 8px #10b98120'
        }}
        title="Your KYC verification is complete"
      >
        <div style={{
          width: '18px',
          height: '18px',
          background: '#10b981',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontSize: '12px',
          fontWeight: 'bold'
        }}>
          {badgeInfo.icon}
        </div>
        <span>{badgeInfo.text}</span>
      </div>
    );
  }

  // Standard Badge for Other States
  return (
    <div
      className="kyc-badge"
      style={{
        borderColor: badgeInfo.color,
        cursor: badgeInfo.isClickable ? 'pointer' : 'default'
      }}
      onClick={badgeInfo.isClickable ? () => navigate('/kyc') : undefined}
    >
      <Shield size={14} style={{ color: badgeInfo.color }} />
      <div className="kyc-badge-content">
        <div className="kyc-badge-text">{badgeInfo.text}</div>
        <div className="kyc-badge-subtext">{badgeInfo.subtext}</div>
      </div>
      {badgeInfo.isClickable && <ArrowRight size={12} className="kyc-badge-arrow" />}
    </div>
  );
});

KYCBadge.displayName = 'KYCBadge';

export default KYCBadge;
